import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import { useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { trackGAEvent, trackMixpanelEvent } from 'utils/analytics';

import { useSignupwWithCreds } from '@/modules/auth/apiHooks';
import { useSignup } from '@/modules/auth/components/FormsContainer/Signup/logic';
import { IAuth } from '@/modules/auth/types';
import {
  Button,
  ErrorMessage,
  InputField,
  Typography,
} from '@/shared/components/atoms';
import routes from '@/shared/constant/routes';
import { useAuth } from '@/shared/hooks/context/auth';

import Header from '../../Header';

import Eye from '~/images/login/Eye.svg';
import EyeSlash from '~/images/login/EyeSlash.svg';

const Signup = () => {
  const { t } = useTranslation('login');

  const { push, pathname } = useRouter();
  const { login } = useAuth();

  const { SignupFormValidationSchema } = useSignup();

  const formMethods = useForm<IAuth.ISignupSchema>({
    mode: 'onChange',
    resolver: yupResolver(SignupFormValidationSchema),
  });

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = formMethods;

  const [passwordVisible, setPasswordVisible] = useState(false);

  const { mutate, isLoading } = useSignupwWithCreds();

  const onSubmit: SubmitHandler<IAuth.ISignupSchema> = (values) => {
    mutate(values, {
      onSuccess: (response) => {
        trackGAEvent({
          event: 'signup_form_submit',
          form_name: 'Signup Form Aydi Eye',
          pathname,
          form_data: {
            email: values.email,
          },
        });
        trackMixpanelEvent('New Sign up Device', response.user);

        login(response.token, response.user, 1727202181651);
      },
    });
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Header title={t('create-your-account')} subtitle={t('quick-setup')} />
        {/* Fields */}
        <div className='flex flex-col gap-16'>
          <InputField
            borderColor='border-outline-variant'
            width='w-full'
            py='!py-14'
            label={t('email-address')}
            placeholder='you@exmple.com'
            error={!!errors.email}
            errorMessage={errors.email?.message}
            className='!rounded-xl bg-background-transparent'
            disabled={isLoading}
            {...register('email')}
          />
          <div className='flex flex-col gap-4'>
            <InputField
              borderColor='border-outline-variant'
              width='w-full'
              py='!py-14'
              label={t('password')}
              placeholder='••••••••'
              error={!!errors.password}
              className='!rounded-xl bg-background-transparent'
              type={passwordVisible ? 'text' : 'password'}
              suffix={
                <div
                  className='cursor-pointer'
                  onClick={() => setPasswordVisible((prev) => !prev)}
                >
                  {passwordVisible ? (
                    <Eye width='24' height='24' />
                  ) : (
                    <EyeSlash width='24' height='24' />
                  )}
                </div>
              }
              disabled={isLoading}
              {...register('password')}
            />
            {errors?.password ? (
              <ErrorMessage
                errorMessage={errors.password.message ?? ''}
                className='mt-0'
              />
            ) : (
              <Typography className='text-surface-10' variant='caption'>
                {t('password-requirements')}
              </Typography>
            )}
          </div>
        </div>
        {/* Submit */}
        <div>
          <Button
            size='full'
            type='submit'
            className='mt-32 w-full !rounded-xl py-14 text-base font-semi-bold'
            disabled={isLoading}
          >
            {t('create-your-account')}
          </Button>
          <Typography
            className='mt-16 text-center text-surface-10'
            variant='caption'
          >
            <Trans
              t={t}
              i18nKey='termsAndConditionsAgreement'
              components={{
                terms: (
                  <a href={routes.terms} className='font-medium underline' />
                ),
                policy: (
                  <a href={routes.policy} className='font-medium underline' />
                ),
              }}
            />
          </Typography>
        </div>
        {/* Login CTA */}
        <div className='mt-40 flex justify-center gap-1'>
          <Typography variant='subtitle2' className='text-surface-10'>
            {t('already-has-account')}
          </Typography>
          <div
            className='cursor-pointer text-14 font-semi-bold text-primary-default underline'
            onClick={() => push(routes.login)}
          >
            {t('login')}
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default Signup;
